<script>
  import { Recaptcha, recaptcha, observer } from 'svelte-recaptcha-v2';
  import Swal from 'sweetalert2';

  import gql from 'graphql-tag';
  import { client } from 'utils/apollo.js';

  import { shoppingCartId } from '../stores/Cart.js';

  import Modal from './Modal.svelte';
  import Input from './Input.svelte';
  import Checkbox from './Checkbox.svelte';
  import Sectionheading from './Sectionheading.svelte';
  import { onMount } from 'svelte';

  export let show = false;
  export let background;

  const googleRecaptchaSiteKey = process.env.RECAPTCHA_SITE_KEY;
  const IMAGE_BASE_URL = process.env.IMAGE_BASE_URL || 'https://images.uniformhero.com.au';

  let showQuoteRecaptcha = false;

  let buttonState = 'disabled';

  // RECAPTCHA EVENTS
  const onCaptchaReady = (event) => {
    buttonState = 'enabled';
  };

  const onCaptchaSuccess = (event) => {
    observer.resolve(event);
    /*
     │If using checkbox method, you can attach your
     │form logic here, or dispatch your custom event.
     */
  };

  const onCaptchaError = (event) => {
    /*
     │Usually due to incorrect siteKey.
     |Make sure you have the correct siteKey..
     */
  };

  const onCaptchaExpire = (event) => {
    /*
     │Normally, you wouldn't need to do anything.
     │Recaptcha should reinit itself automatically.
     */
  };

  const onCaptchaOpen = (event) => {
    /*
     │This fires when the puzzle frame pops.
     */
  };

  const onCaptchaClose = (event) => {
    /*
     │This fires when the puzzle frame closes.
     │Usually happens when the user clicks outside
     |the modal frame.
     */
  };

  function determineDate4WeeksFromNowAndFormat() {
    const date = new Date();
    date.setDate(date.getDate() + 28);
    const day = date.getDate();
    const monthName = date.toLocaleString('default', { month: 'long' });

    function getOrdinal(day) {
      // Special case for teens (11th, 12th, 13th)
      if (day >= 11 && day <= 13) return 'th';

      switch (day % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    }

    return `${day}${getOrdinal(day)} of ${monthName}`;
  }

  const date4WeeksFromNow = determineDate4WeeksFromNowAndFormat();

  async function generateSignedUrl(fileName, mimeType) {
    const QUERY = gql`
      query GenerateSignedUrl($fileName: String!, $mimeType: String!) {
        generatePreSignedUrl(fileName: $fileName, mimeType: $mimeType) {
          url
        }
      }
    `;
    const VARIABLES = {
      fileName: fileName,
      mimeType: mimeType,
    };

    const res = await client.query({
      query: QUERY,
      variables: VARIABLES,
    });

    return res.data.generatePreSignedUrl.url;
  }

  async function quoteRequestSubmit(data) {
    const QUERY = gql`
      query MyQuery(
        $email: String!
        $fileUrls: String
        $message: String!
        $name: String!
        $needBy: String
        $phone: String
        $reason: String
        $recaptchaToken: String!
        $requestCall: Boolean = false
        $shoppingCartId: String!
      ) {
        quoteFormSubmit(
          email: $email
          message: $message
          recaptchaToken: $recaptchaToken
          shoppingCartId: $shoppingCartId
          fileUrls: $fileUrls
          name: $name
          needBy: $needBy
          phone: $phone
          reason: $reason
          requestCall: $requestCall
        ) {
          status
        }
      }
    `;

    const VARIABLES = {
      email: data.email,
      fileUrls: data.fileUrls,
      message: data.message,
      name: data.name,
      needBy: data.needBy,
      phone: data.phone,
      reason: data.reason,
      recaptchaToken: data.recaptchaToken,
      requestCall: data.requestCall,
      shoppingCartId: data.shoppingCartId,
    };

    const res = await client.query({
      query: QUERY,
      variables: VARIABLES,
    });

    return res.data.quoteFormSubmit.status;
  }

  let requestCallCheckbox = false;

  let processing = false;
  const submitHandler = async (e) => {
    processing = true;

    recaptcha.execute();

    const event = await Promise.resolve(observer);

    const recaptchaToken = event.detail?.token ? event.detail.token : false;

    if (!recaptchaToken) {
      return false;
    }

    const formData = new FormData(e.target);

    const data = {};
    for (let field of formData) {
      const [key, value] = field;
      data[key] = value;
    }

    data.requestCall = requestCallCheckbox;
    data.shoppingCartId = $shoppingCartId;
    data.recaptchaToken = recaptchaToken;

    const fileInput = e.target.querySelector('input[name="logos"]');
    let fileUrls = [];

    if (fileInput && fileInput.files && fileInput.files.length > 0) {
      for (const file of fileInput.files) {
        const url = await generateSignedUrl(file.name, file.type);

        // Upload to s3
        await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });

        // Add the url with correct path to the fileUrls array
        const s3LogoUrl = `${IMAGE_BASE_URL}/quote-uploads/${encodeURI(file.name)}`;
        fileUrls.push(s3LogoUrl);
      }
    }

    data.fileUrls = JSON.stringify(fileUrls);

    const status = await quoteRequestSubmit(data);

    if (status === 'success') {
      Swal.fire('Message sent successfully!', 'Our customer service team will respond shortly', 'success');
      e.target.reset();
    } else {
      Swal.fire('Message failed to send. Please try again.', 'Something went wrong - please try again', 'error');
    }
    processing = false;
  };

  onMount(() => {
    showQuoteRecaptcha = true;
  });
</script>

<Modal bind:show fullScreen={true}>
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div class="close" on:click={() => (show = false)}>&times;</div>
  <section class:white={background === 'white'}>
    <Sectionheading subheading="Have a quote and Preview in less than an 1hr" heading="Quote" />

    <div class="container">
      <form action="" on:submit|preventDefault={submitHandler}>
        <div class="group">
          <Input name="name" placeholder="Your Name" required={true} label="Your Name" />
          <Input name="email" placeholder="Your Email" required={true} label="Your Email" type="email" />
        </div>

        <div class="group checkbox">
          <Input
            name="phone"
            placeholder="Optional"
            label="Your Phone Number"
            type="tel"
            required={requestCallCheckbox}
            on:invalid={(event) => {
              if (requestCallCheckbox) {
                event.target.setCustomValidity('Please provide a phone number so we can call you back.');
              }
            }}
            on:input={(event) => {
              event.target.setCustomValidity('');
            }}
          />
          <div class="checkbox-spacer"></div>
          <Checkbox bind:checked={requestCallCheckbox} label="I would like a call to discuss" />
        </div>
        <div class="input">
          <label for="message">Message <small>*</small></label>
          <textarea
            name="message"
            id=""
            rows="10"
            required={true}
            placeholder="E.g. I am looking for 25 navy polos and 25 navy zip hoodies with my logo on the front and back"
          />
        </div>
        <div class="group">
          <Input name="reason" placeholder="E.g. for my sports team" required={false} label="What are the items for" />
          <Input
            name="needBy"
            placeholder={`E.g. ${date4WeeksFromNow}`}
            required={false}
            label="When do you need them"
          />
        </div>
        <div class="group">
          <div class="input">
            <label for="file">Add Logo Files or Designs</label>
            <input type="file" name="logos" id="file" accept="image/*" multiple />
          </div>
        </div>
        <div class="submit">
          {#if buttonState !== 'disabled'}
            <input class="submit-input" type="submit" value="Submit" disabled={processing} />
          {/if}
          {#if show && showQuoteRecaptcha}
            <Recaptcha
              sitekey={googleRecaptchaSiteKey}
              badge={'inline'}
              size={'invisible'}
              on:success={onCaptchaSuccess}
              on:error={onCaptchaError}
              on:expired={onCaptchaExpire}
              on:close={onCaptchaClose}
              on:ready={onCaptchaReady}
            />
          {/if}
        </div>
      </form>
    </div>
  </section>
</Modal>

<style>
  .close {
    display: flex;
    justify-content: flex-end;
    border-radius: 5rem;
    font-size: 5rem;
    font-weight: 300;
    margin-bottom: -5rem;
    cursor: pointer;
  }
  section {
    padding: 10rem 0;
    padding-bottom: 0;
    background-color: #f7f7f7;
  }

  section.white {
    background-color: #fff;
  }

  .container {
    max-width: 140rem;
    width: 90%;
    margin: 10rem auto 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 5rem;
  }

  form {
    display: grid;
    gap: 2rem;
    padding-bottom: 2rem;
  }
  label {
    font-size: 1.8rem;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    margin-bottom: 1.5rem;
  }
  .input {
    display: flex;
    flex-direction: column;
    max-width: 95%;
  }

  .group {
    display: grid;
    grid-template-columns: 1fr;
    gap: 4rem;
  }

  .group.checkbox {
    gap: 1rem;
  }

  .checkbox-spacer {
    display: none;
  }

  small {
    color: #fe5900;
  }

  textarea {
    padding: 1.5rem;
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    border-radius: 0.5rem;
    border: 1px solid #777;
  }

  .submit {
    padding-top: 1rem;
    min-width: 40rem;
    margin-left: auto;
  }

  input[type='submit'] {
    background-color: #fe5900;
    padding: 1.5rem 4rem;
    margin-bottom: 2rem;
    font-family: 'Oswald', sans-serif;
    font-weight: 500;
    color: #fff;
    font-size: 2rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    border: 2px solid #fe5900;
    border-radius: 1%;
    justify-self: baseline;
    cursor: pointer;
    width: 100%;
    transition: background-color 0.2s ease;
  }

  input[type='submit']:hover {
    border: 2px solid #fe5900;
    background-color: transparent;
    color: #000;
  }

  @media screen and (min-width: 400px) {
    .container {
      width: 100%;
      margin: 5rem auto 0 auto;
    }
  }

  @media screen and (min-width: 800px) {
    .container {
      grid-template-columns: repeat(2, 1fr);
    }

    .submit {
      min-width: 100%;
      margin-left: 0;
      margin-right: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    input[type='submit'] {
      width: auto;
      margin-bottom: 0;
    }

    form {
      grid-column-start: 1;
      grid-column-end: 3;
    }

    .group {
      grid-template-columns: repeat(2, 1fr);
    }

    .checkbox-spacer {
      display: block;
    }
  }
</style>
